import type {
  ResourceBuiltinLink,
  ResourceDetailResponse,
  ResourceMapNode,
  ResourceMapRelationType,
  ResourceMapRelationState,
  ResourceMapResponse,
} from "@k8s-dashboard/contracts";

export const BUILTIN_RELATION_NODE_SCAN_CAP = 500;
export const BUILTIN_RELATION_EDGE_SCAN_CAP = 1_500;
export const BUILTIN_RELATION_ROW_CAP = 20;
export const BUILTIN_RELATION_SUPPORT_SCAN_CAP = 8;
export const BUILTIN_RELATION_OMISSION_SCAN_CAP = 1_500;
const OPAQUE_KEY_MAX_BYTES = 1_024;
const NAME_MAX_BYTES = 253;
const NAMESPACE_MAX_BYTES = 63;
const UID_MAX_BYTES = 64;

export type BuiltinRelationQueryState =
  | "loading"
  | "ready"
  | "unsupported"
  | "forbidden"
  | "error"
  | "identity_mismatch";

export type BuiltinRelationGroupId = "pods" | "volume" | "claims" | "volumes";

export interface BuiltinRelationRow {
  key: string;
  kind: "Pod" | "PersistentVolumeClaim" | "PersistentVolume" | "StorageClass";
  namespace?: string;
  name: string;
  uid?: string;
  resolved: boolean;
  link?: ResourceBuiltinLink;
}

export interface BuiltinRelationGroup {
  id: BuiltinRelationGroupId;
  relation: "mounts" | "storage";
  state: ResourceMapRelationState;
  items: BuiltinRelationRow[];
  /** 응답에서 방향·종류가 확인된 고유 관계 수. */
  confirmed: number;
  /** 확인했지만 전역 20행 렌더 상한 때문에 숨긴 후보 수. */
  hidden: number;
  /** 간선 수로 환산할 수 없는 omission/malformed/missing-peer 증거. */
  unknownEvidence: boolean;
  truncated: boolean;
  lowerBound: boolean;
  unresolved: number;
}

export interface RelationWideOmittedEdges {
  relation: "mounts" | "storage";
  count: number;
  lowerBound: boolean;
}

export interface BuiltinRelationsView {
  focusKey: string;
  state: BuiltinRelationQueryState;
  groups: BuiltinRelationGroup[];
  /** 방향별 group에 배분할 수 없는 relation-type 전체 생략 간선 수. */
  relationWideOmittedEdges: RelationWideOmittedEdges[];
}

export interface BuiltinRelationRequest {
  relations: readonly ("mounts" | "storage")[];
}

type RequestedRelation = "mounts" | "storage";
type RelationSpec = readonly [
  id: BuiltinRelationGroupId,
  relation: RequestedRelation,
  peerSide: "from" | "to",
  kind: BuiltinRelationRow["kind"],
  resource: string,
  namespaced: boolean,
];
interface FocusPolicy {
  focus: readonly [
    group: string,
    resource: string,
    kind: string,
    namespaced: boolean,
  ];
  relations: readonly RequestedRelation[];
  specs: readonly RelationSpec[];
}

const POLICIES = [
  {
    focus: ["core", "persistentvolumeclaims", "PersistentVolumeClaim", true],
    relations: ["mounts", "storage"],
    specs: [
      ["pods", "mounts", "from", "Pod", "pods", true],
      [
        "volume",
        "storage",
        "to",
        "PersistentVolume",
        "persistentvolumes",
        false,
      ],
    ],
  },
  {
    focus: ["core", "persistentvolumes", "PersistentVolume", false],
    relations: ["storage"],
    specs: [
      [
        "claims",
        "storage",
        "from",
        "PersistentVolumeClaim",
        "persistentvolumeclaims",
        true,
      ],
    ],
  },
  {
    focus: ["storage.k8s.io", "storageclasses", "StorageClass", false],
    relations: ["storage"],
    specs: [
      [
        "volumes",
        "storage",
        "from",
        "PersistentVolume",
        "persistentvolumes",
        false,
      ],
    ],
  },
] as const satisfies readonly FocusPolicy[];

function policyFor(detail: ResourceDetailResponse): FocusPolicy | undefined {
  if (detail.version !== "v1") return undefined;
  return POLICIES.find(
    ({ focus }) =>
      detail.group === focus[0] &&
      detail.resource === focus[1] &&
      detail.kind === focus[2],
  );
}

export function builtinRelationRequest(
  detail: ResourceDetailResponse | undefined,
): BuiltinRelationRequest | undefined {
  if (!detail?.clusterId || !validName(detail.name) || !validUID(detail.uid))
    return undefined;
  const policy = policyFor(detail);
  if (!policy) return undefined;
  if (
    policy.focus[3]
      ? !validNamespace(detail.namespace)
      : Boolean(detail.namespace)
  )
    return undefined;
  return { relations: policy.relations };
}

export function builtinRelationFocusKey(
  detail: ResourceDetailResponse,
): string {
  return [
    detail.clusterId,
    detail.group,
    detail.version,
    detail.resource,
    detail.namespace ?? "",
    detail.name,
    detail.uid,
  ].join("\u0000");
}

/** URL/list focus와 GET 응답이 완전히 같을 때만 optional map 요청을 허용합니다. */
export function isExactDetailFocus(
  detail: ResourceDetailResponse | undefined,
  clusterId: string,
  gvr: { group: string; version: string; resource: string } | null,
  target: { namespace: string; name: string; uid: string } | null,
): detail is ResourceDetailResponse {
  return Boolean(
    detail &&
    gvr &&
    target &&
    detail.clusterId === clusterId &&
    detail.group === gvr.group &&
    detail.version === gvr.version &&
    detail.resource === gvr.resource &&
    (detail.namespace ?? "") === target.namespace &&
    detail.name === target.name &&
    detail.uid === target.uid,
  );
}

function responseMatches(
  detail: ResourceDetailResponse,
  response: ResourceMapResponse,
): boolean {
  const focus = response.focus;
  return (
    Boolean(focus) &&
    response.clusterId === detail.clusterId &&
    response.appliedScope?.clusterId === detail.clusterId &&
    response.depth === 1 &&
    focus.depth === 0 &&
    focus.group === detail.group &&
    focus.version === detail.version &&
    focus.kind === detail.kind &&
    (focus.namespace ?? "") === (detail.namespace ?? "") &&
    focus.name === detail.name &&
    focus.uid === detail.uid &&
    (!detail.namespace || namespaceInScope(detail.namespace, response))
  );
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function validDNSLabel(value: string): boolean {
  return (
    byteLength(value) <= NAMESPACE_MAX_BYTES &&
    /^[a-z0-9](?:[-a-z0-9]*[a-z0-9])?$/.test(value)
  );
}

function validName(value: unknown): value is string {
  if (typeof value !== "string" || !value || byteLength(value) > NAME_MAX_BYTES)
    return false;
  return value.split(".").every(validDNSLabel);
}

function validNamespace(value: unknown): value is string {
  return typeof value === "string" && Boolean(value) && validDNSLabel(value);
}

function validUID(value: unknown): value is string {
  return (
    typeof value === "string" &&
    Boolean(value) &&
    byteLength(value) <= UID_MAX_BYTES &&
    /^[A-Za-z0-9_.-]+$/.test(value)
  );
}

function validOpaqueKey(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    byteLength(value) <= OPAQUE_KEY_MAX_BYTES &&
    !/[\u0000-\u001f\u007f]/.test(value)
  );
}

function nodeMatches(node: ResourceMapNode, spec: RelationSpec): boolean {
  return (
    node.depth === 1 &&
    node.group === "core" &&
    node.version === "v1" &&
    node.kind === spec[3]
  );
}

function namespaceInScope(
  namespace: string,
  response: ResourceMapResponse,
): boolean {
  const namespaces = response.appliedScope?.namespaces;
  return (
    namespaces === "all" ||
    (Array.isArray(namespaces) && namespaces.includes(namespace))
  );
}

function linkFor(
  node: ResourceMapNode,
  edgeResolved: boolean,
  spec: RelationSpec,
  detail: ResourceDetailResponse,
  response: ResourceMapResponse,
): ResourceBuiltinLink | undefined {
  if (
    !edgeResolved ||
    !node.resolved ||
    !validUID(node.uid) ||
    !validName(node.name)
  )
    return undefined;
  if (spec[5]) {
    if (
      !validNamespace(node.namespace) ||
      !namespaceInScope(node.namespace, response)
    )
      return undefined;
    if (spec[0] === "pods" && node.namespace !== detail.namespace)
      return undefined;
  } else if (node.namespace) {
    return undefined;
  }
  return {
    group: "core",
    version: "v1",
    resource: spec[4],
    kind: spec[3],
    ...(spec[5] ? { namespace: node.namespace } : {}),
    name: node.name,
    uid: node.uid,
  };
}

function statusOf(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null || !("status" in error))
    return undefined;
  return typeof error.status === "number" ? error.status : undefined;
}

function emptyView(
  focusKey: string,
  state: Exclude<BuiltinRelationQueryState, "ready">,
): BuiltinRelationsView {
  return { focusKey, state, groups: [], relationWideOmittedEdges: [] };
}

const CLOSED_RELATIONS: readonly ResourceMapRelationType[] = [
  "owner",
  "selects",
  "routesTo",
  "mounts",
  "scales",
  "protects",
  "storage",
  "serviceAccount",
];
const CLOSED_RELATION_SET = new Set<ResourceMapRelationType>(CLOSED_RELATIONS);

function isKnownRelation(value: unknown): value is ResourceMapRelationType {
  return (
    typeof value === "string" &&
    CLOSED_RELATION_SET.has(value as ResourceMapRelationType)
  );
}

function isRequestedRelation(value: unknown): value is RequestedRelation {
  return value === "mounts" || value === "storage";
}

function isRelationState(value: unknown): value is ResourceMapRelationState {
  return (
    value === "supported" || value === "partial" || value === "unsupported"
  );
}

function boundedRows(value: unknown, cap: number): [unknown[], boolean] {
  const rows = Array.isArray(value) ? value : [];
  return [rows, !Array.isArray(value) || rows.length > cap];
}

function readSupport(
  response: ResourceMapResponse,
  requested: ReadonlySet<RequestedRelation>,
): {
  states: Map<RequestedRelation, ResourceMapRelationState>;
  uncertain: Set<RequestedRelation>;
  malformed: boolean;
} {
  const states = new Map<RequestedRelation, ResourceMapRelationState>();
  const uncertain = new Set<RequestedRelation>();
  const seen = new Set<ResourceMapRelationType>();
  const [rows, overflow] = boundedRows(
    response.relations,
    BUILTIN_RELATION_SUPPORT_SCAN_CAP,
  );
  let malformed = overflow || rows.length !== BUILTIN_RELATION_SUPPORT_SCAN_CAP;
  const scan = Math.min(rows.length, BUILTIN_RELATION_SUPPORT_SCAN_CAP);
  for (let i = 0; i < scan; i += 1) {
    const candidate = rows[i] as unknown;
    if (typeof candidate !== "object" || candidate === null) {
      malformed = true;
      continue;
    }
    const type = (candidate as { type?: unknown }).type;
    const state = (candidate as { state?: unknown }).state;
    if (!isKnownRelation(type) || !isRelationState(state)) {
      malformed = true;
      continue;
    }
    if (seen.has(type)) {
      malformed = true;
      if (isRequestedRelation(type)) uncertain.add(type);
      continue;
    }
    seen.add(type);
    if (isRequestedRelation(type) && requested.has(type)) {
      states.set(type, state);
    }
  }
  if (seen.size !== CLOSED_RELATIONS.length) malformed = true;
  return { states, uncertain, malformed };
}

function readOmissionEvidence(
  response: ResourceMapResponse,
  requested: ReadonlySet<RequestedRelation>,
): { relations: Set<RequestedRelation>; malformed: boolean } {
  const relations = new Set<RequestedRelation>();
  const [rows, malformedAtBoundary] = boundedRows(
    response.omissions,
    BUILTIN_RELATION_OMISSION_SCAN_CAP,
  );
  let malformed = malformedAtBoundary;
  const scan = Math.min(rows.length, BUILTIN_RELATION_OMISSION_SCAN_CAP);
  for (let i = 0; i < scan; i += 1) {
    const omission = rows[i] as unknown;
    if (typeof omission !== "object" || omission === null) {
      malformed = true;
      continue;
    }
    const type = (omission as { type?: unknown }).type;
    const node = (omission as { node?: unknown }).node;
    const reason = (omission as { reason?: unknown }).reason;
    if (
      !isKnownRelation(type) ||
      !validOpaqueKey(node) ||
      typeof reason !== "string" ||
      reason.length === 0
    ) {
      malformed = true;
      continue;
    }
    /* 한 omission은 간선 한 개가 아니라 "계산하지 못한 관계" 증거입니다. */
    if (isRequestedRelation(type) && requested.has(type)) relations.add(type);
  }
  return { relations, malformed };
}

function readRelationWideOmitted(
  response: ResourceMapResponse,
  requested: ReadonlySet<RequestedRelation>,
): { items: RelationWideOmittedEdges[]; malformed: boolean } {
  const items: RelationWideOmittedEdges[] = [];
  let malformed = false;
  const raw = response.truncation?.byRelation as unknown;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return { items, malformed: true };
  let scanned = 0;
  for (const key in raw) {
    if (!Object.prototype.hasOwnProperty.call(raw, key)) continue;
    scanned += 1;
    if (scanned > BUILTIN_RELATION_SUPPORT_SCAN_CAP) {
      malformed = true;
      break;
    }
    const value = (raw as Record<string, unknown>)[key];
    if (
      !isKnownRelation(key) ||
      typeof value !== "number" ||
      !Number.isSafeInteger(value) ||
      value < 0
    ) {
      malformed = true;
      continue;
    }
    if (
      isRequestedRelation(key) &&
      requested.has(key) &&
      (value as number) > 0
    ) {
      items.push({
        relation: key,
        count: value as number,
        lowerBound: response.truncation?.lowerBound === true,
      });
    }
  }
  return { items, malformed };
}

export function selectBuiltinRelations(
  detail: ResourceDetailResponse,
  input: {
    capability: boolean;
    loading: boolean;
    error: unknown;
    response?: ResourceMapResponse;
  },
): BuiltinRelationsView | undefined {
  const policy = policyFor(detail);
  if (!policy) return undefined;
  const focusKey = builtinRelationFocusKey(detail);
  if (!input.capability) return emptyView(focusKey, "unsupported");
  if (input.error)
    return emptyView(
      focusKey,
      statusOf(input.error) === 403 ? "forbidden" : "error",
    );
  if (!input.response)
    return emptyView(focusKey, input.loading ? "loading" : "error");
  if (!responseMatches(detail, input.response))
    return emptyView(focusKey, "identity_mismatch");

  const response = input.response;
  const specs = policy.specs;
  const requested = new Set(policy.relations);
  const support = readSupport(response, requested);
  const omissionEvidence = readOmissionEvidence(response, requested);
  const relationWide = readRelationWideOmitted(response, requested);
  const serverLowerBound = response.truncation?.lowerBound === true;
  const serverTruncated = response.truncated === true;
  let globallyUncertain =
    support.malformed ||
    omissionEvidence.malformed ||
    relationWide.malformed ||
    typeof response.truncated !== "boolean" ||
    typeof response.truncation !== "object" ||
    response.truncation === null;
  const [nodeRows, nodeOverflow] = boundedRows(
    response.nodes,
    BUILTIN_RELATION_NODE_SCAN_CAP,
  );
  const [edgeRows, edgeOverflow] = boundedRows(
    response.edges,
    BUILTIN_RELATION_EDGE_SCAN_CAP,
  );
  globallyUncertain ||= nodeOverflow || edgeOverflow;
  const nodes = new Map<string, ResourceMapNode>();
  for (
    let i = 0;
    i < nodeRows.length && i < BUILTIN_RELATION_NODE_SCAN_CAP;
    i += 1
  ) {
    const node = nodeRows[i] as ResourceMapNode;
    if (!node || !validOpaqueKey(node.key)) {
      globallyUncertain = true;
      continue;
    }
    if (nodes.has(node.key)) {
      globallyUncertain = true;
      continue;
    }
    nodes.set(node.key, node);
  }

  const groups = specs.map<BuiltinRelationGroup>((spec) => ({
    id: spec[0],
    relation: spec[1],
    state: support.states.get(spec[1]) ?? "unsupported",
    items: [],
    confirmed: 0,
    hidden: 0,
    unknownEvidence:
      omissionEvidence.relations.has(spec[1]) || support.uncertain.has(spec[1]),
    truncated: false,
    lowerBound: serverLowerBound,
    unresolved: 0,
  }));
  const seen = specs.map(() => new Set<string>());
  let rendered = 0;

  for (
    let i = 0;
    i < edgeRows.length && i < BUILTIN_RELATION_EDGE_SCAN_CAP;
    i += 1
  ) {
    const edge = edgeRows[i] as unknown;
    if (
      typeof edge !== "object" ||
      edge === null ||
      !validOpaqueKey((edge as { from?: unknown }).from) ||
      !validOpaqueKey((edge as { to?: unknown }).to) ||
      !isRequestedRelation((edge as { type?: unknown }).type) ||
      typeof (edge as { resolved?: unknown }).resolved !== "boolean"
    ) {
      globallyUncertain = true;
      continue;
    }
    const validEdge = edge as ResourceMapResponse["edges"][number];
    for (let specIndex = 0; specIndex < specs.length; specIndex += 1) {
      const spec = specs[specIndex];
      const group = groups[specIndex];
      if (group.state === "unsupported" || validEdge.type !== spec[1]) continue;
      const focusSide = spec[2] === "from" ? validEdge.to : validEdge.from;
      if (focusSide !== response.focus.key) continue;
      const peerKey = spec[2] === "from" ? validEdge.from : validEdge.to;
      const peer = nodes.get(peerKey);
      if (!peer) {
        group.unknownEvidence = true;
        continue;
      }
      if (!nodeMatches(peer, spec) || seen[specIndex].has(peerKey)) continue;
      if (
        typeof peer.resolved !== "boolean" ||
        typeof peer.name !== "string" ||
        (peer.uid !== undefined && typeof peer.uid !== "string") ||
        (peer.namespace !== undefined && typeof peer.namespace !== "string")
      ) {
        group.unknownEvidence = true;
        continue;
      }
      seen[specIndex].add(peerKey);
      group.confirmed += 1;
      const resolved = validEdge.resolved && peer.resolved;
      if (!resolved) group.unresolved += 1;
      if (rendered >= BUILTIN_RELATION_ROW_CAP) {
        group.hidden += 1;
        group.truncated = true;
        continue;
      }
      if (!validOpaqueKey(peer.name)) {
        group.unknownEvidence = true;
        continue;
      }
      const link = linkFor(peer, validEdge.resolved, spec, detail, response);
      if (resolved && !link) group.unknownEvidence = true;
      group.items.push({
        key: peer.key,
        kind: spec[3],
        ...(peer.namespace ? { namespace: peer.namespace } : {}),
        name: peer.name,
        ...(peer.uid ? { uid: peer.uid } : {}),
        resolved,
        link,
      });
      rendered += 1;
    }
  }

  const localScanTruncated = nodeOverflow || edgeOverflow;
  for (const group of groups) {
    const relationWideOmission = relationWide.items.some(
      (item) => item.relation === group.relation,
    );
    group.unknownEvidence =
      group.unknownEvidence || globallyUncertain || relationWideOmission;
    group.truncated = group.truncated || serverTruncated || localScanTruncated;
    group.lowerBound =
      group.lowerBound ||
      serverTruncated ||
      localScanTruncated ||
      group.unknownEvidence ||
      group.state === "partial";
    if (
      group.state === "supported" &&
      (group.unknownEvidence || group.truncated)
    )
      group.state = "partial";
  }

  return {
    focusKey,
    state: "ready",
    groups,
    relationWideOmittedEdges: relationWide.items,
  };
}
